import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, AlertCircle, Mail } from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading, success, error, expired, already_verified
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Token de vérification manquant.');
      return;
    }

    verifyEmail();
  }, [token]);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const verifyEmail = async () => {
    try {
      setStatus('loading');

      const response = await axios.get(`${API_URL}/auth/verify-email/${token}`);

      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message);
        setEmail(response.data.user?.email || '');

        // Redirection automatique après 3 secondes
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Email vérifié ! Vous pouvez maintenant vous connecter.'
            }
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Erreur vérification:', error);

      const errorData = error.response?.data;
      const errorCode = errorData?.code;

      if (errorCode === 'VERIFICATION_FAILED') {
        const errorMsg = errorData.message;

        if (errorMsg.includes('expiré')) {
          setStatus('expired');
          setMessage('Le lien de vérification a expiré.');
        } else if (errorMsg.includes('déjà été vérifié')) {
          setStatus('already_verified');
          setMessage('Cet email a déjà été vérifié.');
        } else {
          setStatus('error');
          setMessage(errorMsg || 'Le lien de vérification est invalide.');
        }
      } else if (errorCode === 'TOO_MANY_ATTEMPTS') {
        setStatus('error');
        setMessage('Trop de tentatives. Veuillez patienter avant de réessayer.');
        setResendCooldown(3600); // 1 heure
      } else {
        setStatus('error');
        setMessage(errorData?.message || 'Erreur lors de la vérification de votre email.');
      }
    }
  };

  const handleResendEmail = async () => {
    if (!email && status !== 'expired') {
      setMessage('Veuillez entrer votre adresse email.');
      return;
    }

    try {
      setResendLoading(true);

      const response = await axios.post(`${API_URL}/auth/resend-verification`, {
        email: email
      });

      if (response.data.success) {
        setMessage('Un nouveau lien de vérification a été envoyé à votre adresse email.');
        setResendCooldown(300); // 5 minutes cooldown
      }
    } catch (error) {
      console.error('Erreur renvoi email:', error);

      const errorData = error.response?.data;
      const errorCode = errorData?.code;

      if (errorCode === 'TOO_MANY_RESEND_ATTEMPTS') {
        setMessage('Trop de demandes. Veuillez vérifier vos spams ou attendre 24 heures.');
        setResendCooldown(86400); // 24 heures
      } else if (errorCode === 'TOO_SOON') {
        const match = errorData.message.match(/(\d+) minute/);
        const minutes = match ? parseInt(match[1]) : 5;
        setResendCooldown(minutes * 60);
        setMessage(errorData.message);
      } else if (errorCode === 'ALREADY_VERIFIED') {
        setStatus('already_verified');
        setMessage('Cet email est déjà vérifié. Vous pouvez vous connecter.');
      } else {
        setMessage(errorData?.message || 'Erreur lors du renvoi de l\'email.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  const formatCooldown = (seconds) => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    } else if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}m ${secs}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">

        {/* Status Icon */}
        <div className="flex justify-center mb-6">
          {status === 'loading' && (
            <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
            </div>
          )}

          {status === 'success' && (
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-scale-in">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          )}

          {(status === 'error' || status === 'expired') && (
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
          )}

          {status === 'already_verified' && (
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-blue-600" />
            </div>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-center mb-4 text-gray-900">
          {status === 'loading' && 'Vérification en cours...'}
          {status === 'success' && 'Email vérifié !'}
          {status === 'error' && 'Vérification échouée'}
          {status === 'expired' && 'Lien expiré'}
          {status === 'already_verified' && 'Déjà vérifié'}
        </h1>

        {/* Message */}
        <p className="text-center text-gray-600 mb-6">
          {status === 'loading' && 'Veuillez patienter pendant que nous vérifions votre adresse email...'}
          {message}
        </p>

        {/* Success Actions */}
        {status === 'success' && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
              <p className="font-medium">Votre compte est maintenant actif !</p>
              <p className="mt-1">Vous allez être redirigé vers la page de connexion...</p>
            </div>

            <Link
              to="/login"
              className="block w-full bg-gradient-to-r from-primary-600 to-purple-600 text-white py-3 rounded-lg font-semibold text-center hover:from-primary-700 hover:to-purple-700 transition-all"
            >
              Se connecter maintenant
            </Link>
          </div>
        )}

        {/* Already Verified Actions */}
        {status === 'already_verified' && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
              <p className="font-medium">Votre email est déjà vérifié.</p>
              <p className="mt-1">Vous pouvez vous connecter dès maintenant.</p>
            </div>

            <Link
              to="/login"
              className="block w-full bg-gradient-to-r from-primary-600 to-purple-600 text-white py-3 rounded-lg font-semibold text-center hover:from-primary-700 hover:to-purple-700 transition-all"
            >
              Se connecter
            </Link>
          </div>
        )}

        {/* Expired/Error Actions */}
        {(status === 'expired' || (status === 'error' && !token)) && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              <p className="font-medium">Lien de vérification expiré ou invalide</p>
              <p className="mt-1">Les liens de vérification sont valables 24 heures. Demandez-en un nouveau ci-dessous.</p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Adresse email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={resendLoading || resendCooldown > 0}
              />
            </div>

            <button
              onClick={handleResendEmail}
              disabled={resendLoading || resendCooldown > 0 || !email}
              className="w-full bg-gradient-to-r from-primary-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {resendLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Envoi en cours...
                </>
              ) : resendCooldown > 0 ? (
                <>
                  <Mail className="w-5 h-5" />
                  Renvoyer dans {formatCooldown(resendCooldown)}
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Renvoyer l'email de vérification
                </>
              )}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Retour à la connexion
              </Link>
            </div>
          </div>
        )}

        {/* Generic Error Actions */}
        {status === 'error' && token && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
              <p className="font-medium">Que faire ?</p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li>Vérifiez que vous avez cliqué sur le bon lien</li>
                <li>Vérifiez que le lien n'a pas été coupé dans votre email</li>
                <li>Demandez un nouveau lien de vérification</li>
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={verifyEmail}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Réessayer
              </button>
              <Link
                to="/login"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-center"
              >
                Connexion
              </Link>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-center text-gray-500">
            Besoin d'aide ?{' '}
            <Link to="/contact" className="text-primary-600 hover:text-primary-700 font-medium">
              Contactez le support
            </Link>
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes scale-in {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        .animate-scale-in {
          animation: scale-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
